/**
 * Luna Intelligence side panel.
 *
 * Chat UI with streaming assistant responses, inline tool traces
 * (showing when Luna reads files / searches / proposes writes), and
 * embedded diff approval cards for every proposed change.
 *
 * The panel auto-captures editor context on each send: the page the
 * user has open, the selected section, and the currently-open file in
 * code mode. That lets the user say "aplica desconto de 10% no badge
 * deste card" without spelling out which file to touch — Luna sees
 * what the user is looking at.
 */

import { useEffect, useRef, useState } from 'react';
import { useLuna, type ChatTurn, type ToolTrace } from '../../state/luna-store';
import { useEditor } from '../../state/store';
import { useCode } from '../../state/code-store';
import { ApprovalCard } from './ApprovalCard';
import { BuyCreditsModal } from './BuyCreditsModal';

export function LunaPanel() {
  const turns = useLuna((s) => s.turns);
  const proposals = useLuna((s) => s.proposals);
  const sending = useLuna((s) => s.sending);
  const balance = useLuna((s) => s.balance);
  const send = useLuna((s) => s.send);
  const cancel = useLuna((s) => s.cancel);
  const openBuyCredits = useLuna((s) => s.openBuyCredits);

  const lowBalance =
    balance && !balance.unlimited && balance.balance < 100;

  // Editor context that goes with every send
  const page = useEditor((s) => s.page);
  const selection = useEditor((s) => s.selection);
  const globalSettings = useEditor((s) => s.globalSettings);
  const activeFilePath = useCode((s) => s.activePath);
  const activeTab = useCode((s) => (s.activePath ? s.tabs[s.activePath] : null));

  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll on new messages / streaming deltas
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [turns, proposals]);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const text = input;
    setInput('');
    void send(text, {
      page,
      selectedSectionId:
        selection && 'sectionId' in selection ? selection.sectionId : undefined,
      openFile: activeTab
        ? { path: activeFilePath || '', selection: undefined }
        : undefined,
      globalSettings,
    });
  }

  return (
    <aside className="flex h-full w-[360px] shrink-0 flex-col border-l border-border bg-surface">
      <header className="flex items-center gap-2 border-b border-border px-4 py-3">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white shadow-sm">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M6 6 4.5 4.5M19.5 19.5 18 18M6 18l-1.5 1.5M19.5 4.5 18 6" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wider text-text-subdued">
            Luna Intelligence
          </div>
          <div className="flex items-center gap-1.5 text-[13px] font-semibold text-text">
            Assistente do tema
          </div>
        </div>
        {balance && (
          <button
            onClick={() => void openBuyCredits()}
            className="flex items-center gap-1 rounded-full bg-surface-subdued px-2 py-1 text-[11px] font-medium text-text hover:bg-surface-hovered"
            title={
              balance.unlimited
                ? 'Modo desenvolvedor — créditos ilimitados (clique para ver pacotes)'
                : `Saldo: ${balance.balance} créditos — clique para comprar mais`
            }
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" className="text-yellow-500">
              <path d="M12 2 8 9l-7 1 5 5-1 7 7-4 7 4-1-7 5-5-7-1z" />
            </svg>
            {balance.unlimited ? '∞' : balance.balance.toLocaleString('pt-BR')}
            <span className="ml-0.5 text-interactive">+</span>
          </button>
        )}
      </header>

      {lowBalance && (
        <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-[12px] text-amber-900">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <path d="M12 9v4M12 17h.01" />
          </svg>
          <span className="flex-1">
            Saldo baixo: <b>{balance!.balance}</b> créditos restantes.
          </span>
          <button
            onClick={() => void openBuyCredits()}
            className="rounded bg-amber-600 px-2 py-0.5 text-[11px] font-semibold text-white hover:brightness-110"
          >
            Comprar
          </button>
        </div>
      )}

      <div ref={scrollRef} className="lc-scroll flex-1 overflow-y-auto px-3 py-4">
        {turns.length === 0 ? <EmptyState /> : null}
        {turns.map((t) => (
          <Turn key={t.id} turn={t} proposals={proposals.filter((p) => p.turnId === t.id)} />
        ))}
        {sending && (
          <div className="mt-2 flex items-center gap-2 text-[12px] text-text-subdued">
            <span className="lc-spin h-3 w-3 rounded-full border border-text-subdued/30 border-t-text-subdued" />
            Luna está pensando…
            <button
              onClick={cancel}
              className="ml-auto text-[11px] text-interactive hover:underline"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} className="border-t border-border px-3 py-3">
        <div className="relative">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit(e as any);
              }
            }}
            placeholder={
              sending
                ? 'Aguardando Luna…'
                : 'Peça para a Luna mudar algo no seu tema'
            }
            disabled={sending}
            rows={3}
            className="lc-input min-h-[72px] resize-none pr-12 text-[13px]"
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            className="absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center rounded-md bg-interactive text-white transition-opacity hover:brightness-110 disabled:opacity-40"
            title="Enviar (Enter)"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m3 3 3 9-3 9 19-9Z" />
              <path d="M6 12h16" />
            </svg>
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-text-subdued">
          Enter envia • Shift+Enter nova linha • Alterações pedem sua aprovação
        </p>
      </form>

      <BuyCreditsModal />
    </aside>
  );
}

function EmptyState() {
  return (
    <div className="mt-4 rounded-lg border border-dashed border-border bg-surface-subdued p-4">
      <div className="mb-2 text-[13px] font-semibold text-text">Oi, eu sou a Luna ✨</div>
      <p className="text-[12px] leading-snug text-text-subdued">
        Posso editar arquivos do seu tema, explicar trechos de código, criar
        seções novas ou consertar bugs. Toda alteração que eu propuser passa
        pela sua aprovação antes de ser aplicada.
      </p>
      <div className="mt-3 space-y-1.5">
        {[
          'Adicione um banner de frete grátis no topo do header',
          'Explique o que o snippet card-produto.liquid faz',
          'Traduza os textos da seção hero pro inglês',
        ].map((example) => (
          <Example key={example} text={example} />
        ))}
      </div>
    </div>
  );
}

function Example({ text }: { text: string }) {
  const send = useLuna((s) => s.send);
  const page = useEditor((s) => s.page);
  const selection = useEditor((s) => s.selection);
  const globalSettings = useEditor((s) => s.globalSettings);
  return (
    <button
      onClick={() =>
        void send(text, {
          page,
          selectedSectionId:
            selection && 'sectionId' in selection ? selection.sectionId : undefined,
          globalSettings,
        })
      }
      className="block w-full rounded border border-border bg-surface px-2.5 py-1.5 text-left text-[12px] text-text-subdued transition-colors hover:border-interactive/40 hover:bg-surface-subdued hover:text-text"
    >
      "{text}"
    </button>
  );
}

// ---------------------------------------------------------------------------
// Turn rendering
// ---------------------------------------------------------------------------

function Turn({
  turn,
  proposals,
}: {
  turn: ChatTurn;
  proposals: ReturnType<typeof useLuna.getState>['proposals'];
}) {
  if (turn.role === 'user') {
    return (
      <div className="mb-3 flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-interactive px-3 py-2 text-[13px] text-white">
          {turn.text}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3">
      {turn.tools.length > 0 && (
        <div className="mb-1 space-y-1">
          {turn.tools.map((t) => (
            <ToolRow key={t.id} trace={t} />
          ))}
        </div>
      )}
      {turn.text && (
        <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-text">
          {turn.text}
          {turn.streaming && <span className="ml-0.5 inline-block h-3 w-1 animate-pulse bg-interactive align-middle" />}
        </div>
      )}
      {proposals.length > 0 && (
        <div className="mt-1.5">
          {proposals.map((p) => (
            <ApprovalCard key={p.proposalId} proposal={p} />
          ))}
        </div>
      )}
      {turn.error && (
        <div className="mt-2 rounded border border-critical/30 bg-critical/5 px-3 py-2 text-[12px] text-critical">
          {turn.error}
        </div>
      )}
    </div>
  );
}

function ToolRow({ trace }: { trace: ToolTrace }) {
  const label = toolLabel(trace);
  const done = !!trace.summary;
  return (
    <div
      className="flex items-center gap-2 rounded border border-border/50 bg-surface-subdued/60 px-2 py-1 text-[11.5px] text-text-subdued"
      title={JSON.stringify(trace.input)}
    >
      {done ? (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-green-600">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      ) : (
        <span className="lc-spin h-2.5 w-2.5 rounded-full border border-text-subdued/30 border-t-text-subdued" />
      )}
      <span className="truncate">{label}</span>
      {trace.summary && (
        <span className="ml-auto shrink-0 text-[10.5px] opacity-70">
          {trace.summary}
        </span>
      )}
    </div>
  );
}

function toolLabel(t: ToolTrace): string {
  if (t.name === 'list_files') return 'Listando arquivos do tema';
  if (t.name === 'read_file') return `Lendo ${t.input?.path || '…'}`;
  if (t.name === 'search_files') return `Buscando "${t.input?.query || ''}"`;
  if (t.name === 'propose_write') return `Propondo editar ${t.input?.path || '…'}`;
  if (t.name === 'propose_delete') return `Propondo deletar ${t.input?.path || '…'}`;
  return t.name;
}
